import * as React from "react";
import { listPublishes, publish, type PublishRow } from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmModal } from "@/components/ui/modal";
import { Notice, Warnings } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonRows } from "@/components/ui/skeleton";
import { TBody, TD, TEmpty, TH, THead, TR, TableShell } from "@/components/ui/table";

/**
 * Asking for a build.
 *
 * THE WORD IS MISLEADING AND THE SCREEN HAS TO UNDO IT. Publishing does not
 * decide what the site says -- the storefront's build reads the live rows, so
 * every saved edit is already on its way to the next build whether or not
 * anybody pressed this. What this does is START that build, now, and record
 * what the shop was saying at the moment somebody asked.
 *
 * An operator who believes otherwise will save an urgent price change, not
 * press Publish, and be reassured that nothing has gone out. So the difference
 * is stated at the top of the screen rather than in a tooltip.
 *
 * THERE IS NO COOLDOWN HERE EITHER. Two presses start two builds; the worker
 * says why that is a decision and not an oversight. The confirmation dialog is
 * what stands in for it -- not as a gate, but because "I meant to press
 * staging" is the mistake this screen actually produces.
 */
const KINDS = [
  {
    kind: "site" as const,
    label: "Publish the site",
    blurb: "Builds and deploys esimwithnumber.com. Visitors see the result.",
    variant: "default" as const,
  },
  {
    kind: "staging" as const,
    label: "Build staging",
    blurb: "The same build, deployed where only you can see it.",
    variant: "outline" as const,
  },
];

export function PublishScreen() {
  const { data, loading, error, reload } = useAsync(() => listPublishes(), []);
  const action = useAction();
  const [confirm, setConfirm] = React.useState<"site" | "staging" | null>(null);

  const rows = data?.publishes ?? [];

  async function go(kind: "site" | "staging") {
    await action.run(() => publish(kind));
    setConfirm(null);
    reload();
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Publish"
        description="Start a build of the storefront, and see the builds already asked for."
      />

      <Notice tone="info" title="Saving already publishes.">
        Every save that changes the storefront -- a package, a page&apos;s SEO, a setting the site
        shows -- starts a build on its own. Press this to run one again by hand, for instance after
        a build failed.
      </Notice>

      {error && <Notice tone="error">{error}</Notice>}
      {action.error && <Notice tone="error">{action.error}</Notice>}
      <Warnings warnings={action.warnings} />

      <div className="grid gap-4 sm:grid-cols-2">
        {KINDS.map((k) => (
          <Card key={k.kind} className="flex flex-col justify-between gap-3 p-4">
            <div>
              <div className="text-sm font-semibold">{k.label}</div>
              <p className="mt-1 text-xs text-muted-foreground">{k.blurb}</p>
            </div>
            <div>
              <Button variant={k.variant} disabled={action.busy} onClick={() => setConfirm(k.kind)}>
                {action.busy ? "Asking…" : k.label}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        {loading && rows.length === 0 ? (
          <SkeletonRows rows={4} cols={4} />
        ) : (
          <TableShell>
            <THead>
              <TH>Asked for</TH>
              <TH>What</TH>
              <TH>GitHub</TH>
              <TH>Record of the words</TH>
            </THead>
            <TBody>
              {rows.length === 0 && <TEmpty colSpan={4}>No build has been asked for yet.</TEmpty>}
              {rows.map((row) => (
                <PublishTableRow key={row.id} row={row} />
              ))}
            </TBody>
          </TableShell>
        )}
      </Card>

      <ConfirmModal
        open={confirm !== null}
        title={confirm === "site" ? "Build and deploy the site?" : "Build staging?"}
        confirmLabel={confirm === "site" ? "Publish the site" : "Build staging"}
        busy={action.busy}
        onClose={() => setConfirm(null)}
        body={
          confirm === "site" ? (
            <>
              <p>
                This starts a build of esimwithnumber.com from everything saved right now, and
                deploys it when it finishes.
              </p>
              <p className="mt-2">
                It cannot be called back. Publishing again with the old words is how a mistake is
                undone.
              </p>
            </>
          ) : (
            <p>This starts the same build, deployed where only you can see it.</p>
          )
        }
        onConfirm={() => confirm && void go(confirm)}
      />
    </div>
  );
}

/**
 * One asked-for build.
 *
 * `dispatchStatus` is GITHUB'S answer to the request, not the build's outcome
 * -- a 204 means GitHub accepted the job, and the job can still fail
 * afterwards. Saying "accepted" rather than "published" is the difference, and
 * it is the one an operator watching for a deploy needs.
 */
function PublishTableRow({ row }: { row: PublishRow }) {
  return (
    <TR>
      <TD className="whitespace-nowrap">{when(row.createdAt)}</TD>
      <TD>
        <Badge color={row.kind === "site" ? "blue" : "gray"}>{row.kind}</Badge>
      </TD>
      <TD>
        {row.dispatchError ? (
          <span className="text-destructive-ink">{row.dispatchError}</span>
        ) : row.dispatchStatus === null ? (
          <span className="text-muted-foreground">no answer</span>
        ) : (
          <Badge color="green">accepted</Badge>
        )}
      </TD>
      <TD className="text-muted-foreground">
        {/* Ninety days, then the words are gone and the row stays. The row is
            the evidence something was done; the snapshot is only the copy. */}
        {row.snapshotKept ? "kept" : "expired"}
      </TD>
    </TR>
  );
}
